import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiUser, FiLock, FiLogIn, FiLogOut } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";

const Admin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const fontClass = i18n.language === "ar" ? "font-['Amiri']" : "font-sans";

  const pageTransition = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      navigate("/admin-overview");
    }

    if (i18n.language === "ar") {
      document.body.classList.add("rtl-layout");
    } else {
      document.body.classList.remove("rtl-layout");
    }

    return () => {
      document.body.classList.remove("rtl-layout");
    };
  }, [navigate, i18n.language]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/login`,
        { username, password }
      );
      if (!response.data.user?.isActive) {
        setError(t("admin.inactiveUser"));
        setIsLoading(false);
        return;
      }
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("adminRole", response.data.user.role);

      setTimeout(() => {
        navigate("/admin-overview");
        window.location.reload();
      }, 300);

    } catch (err) {
      if (err.response?.status === 403) {
        setError(t("admin.inactiveUser"));
      } else {
        setError(
          err.response?.status === 401
            ? t("admin.invalidCredentials")
            : err.response?.data?.message || t("admin.invalidCredentials")
        );
      }
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/logout`);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRole");

      setTimeout(() => {
        navigate("/admin");
        window.location.reload();
      }, 300);

    } catch (err) {
      console.error("Logout error:", err);
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageTransition}
      transition={{ duration: 0.4 }}
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
      className="mt-12 sm:mt-16 md:mt-20 flex items-center justify-center min-h-[calc(100vh-4rem)] px-4"
    >
      <motion.div
        className="bg-white p-6 sm:p-10 rounded-2xl shadow-md w-full max-w-[400px] border border-gray-200"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300, damping: 15 }}
      >
        <div className={`mb-8 ${i18n.language === "ar" ? "text-right" : "text-center"}`}>
          <motion.div
            className="w-20 h-20 bg-[#333] rounded-full mx-auto flex items-center justify-center mb-4"
            whileHover={{ rotate: 10 }}
            transition={{ duration: 0.2 }}
          >
            <FiUser className="text-4xl text-white" />
          </motion.div>
          <h2 className={`text-2xl flex items-center justify-center sm:text-3xl font-bold text-[#333] ${fontClass}`}>
            {t("admin.login")}
          </h2>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded ${i18n.language === "ar" ? "border-l-0 border-r-4" : ""}`}
          >
            <p className={`flex items-center ${i18n.language === "ar" ? "flex-row-reverse" : ""}`}>
              <span className={i18n.language === "ar" ? "ml-2" : "mr-2"}>⚠️</span> {error}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="relative">
            <FiUser
              className={`absolute top-1/2 transform -translate-y-1/2 text-gray-400 left-3`}
            />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={`w-full py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#333] focus:border-transparent text-[#333] focus:outline-none ${fontClass} ${i18n.language === "ar" ? "text-right pl-12 pr-4" : "text-left pl-12 pr-4"
                }`}
              placeholder={t("admin.username")}
              required
              disabled={isLoading}
            />
          </div>

          <div className="relative">
            <FiLock
              className={`absolute top-1/2 transform -translate-y-1/2 text-gray-400 left-3`}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#333] focus:border-transparent text-[#333] focus:outline-none ${fontClass} ${i18n.language === "ar" ? "text-right pl-12 pr-4" : "text-left pl-12 pr-4"
                }`}
              placeholder={t("admin.password")}
              required
              disabled={isLoading}
            />
          </div>

          <motion.button
            type="submit"
            disabled={isLoading}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`w-full bg-[#333] text-white py-3 rounded-lg flex items-center justify-center ${i18n.language === "ar" ? "flex-row-reverse space-x-2 space-x-reverse" : "space-x-2"
              } hover:bg-gray-700 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span className={`${fontClass} ${i18n.language === "ar" ? "pl-2" : "pr-2"}`}>
                  {t("admin.loading")}
                </span>
              </>
            ) : (
              <>
                <FiLogIn className="text-xl" />
                <span className={`${fontClass} ${i18n.language === "ar" ? "pl-2" : "pr-2"}`}>{t("admin.login")}</span>
              </>
            )}
          </motion.button>
        </form>

        {localStorage.getItem("adminToken") && (
          <motion.button
            onClick={handleLogout}
            disabled={isLoading}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`w-full mt-4 bg-red-500 text-white py-3 rounded-lg flex items-center justify-center ${i18n.language === "ar" ? "flex-row-reverse space-x-2 space-x-reverse" : "space-x-2"
              } hover:bg-red-600 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span className={`${fontClass} ${i18n.language === "ar" ? "ml-2" : "ml-2"}`}>
                  {t("admin.loading")}
                </span>
              </>
            ) : (
              <>
                <FiLogOut className="text-xl" />
                <span className={fontClass}>{t("admin.logout")}</span>
              </>
            )}
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Admin;